import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Select, Input, message } from "antd";
import {
    SendOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Modal } from "../../design-system";
import ScreenshotUpload from "./ScreenshotUpload";
import { submitFeedback, validateFeedbackDraft } from "./feedbackApi";
import "./HelpFeedback.scss";
import logger from "../../utils/logger";



const PRIORITY_OPTIONS = [
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "CRITICAL", label: "Critical" },
];

// Simplified ReactQuill toolbar for feedback descriptions
const QUILL_MODULES = {
    toolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
    ],
    clipboard: { matchVisual: false },
};

const QUILL_FORMATS = ["bold", "italic", "underline", "list", "link"];

/**
 * Help & Feedback Modal
 *
 * Unified form for bug reports and feature requests.
 * Submits directly to create a task in the Tech Team queue.
 * HRP Agent handles triage and classification automatically.
 */
const HelpFeedbackModal = ({ visible, onClose }) => {
    const navigate = useNavigate();

    // Form state
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState("MEDIUM");
    const [screenshots, setScreenshots] = useState([]);

    // UI state
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    // Completeness guidance from /help-feedback/drafts/validate — advisory, never a block
    const [validating, setValidating] = useState(false);
    const [guidance, setGuidance] = useState(null);

    // Environment info — captured fresh each time modal opens
    const [envInfo, setEnvInfo] = useState({});

    // Reset form when modal opens/closes
    const resetForm = useCallback(() => {
        setTitle("");
        setDescription("");
        setPriority("MEDIUM");
        setScreenshots([]);
        setSubmitResult(null);
        setGuidance(null);
    }, []);

    useEffect(() => {
        if (visible) {
            resetForm();
            // Capture environment at the moment the modal opens
            setEnvInfo({
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                language: navigator.language,
            });
        }
    }, [visible, resetForm]);

    const isDescriptionEmpty = () => {
        if (!description) return true;
        const text = description.replace(/<[^>]*>/g, "").trim();
        return text.length === 0;
    };

    const canSubmit = () => {
        return title.trim().length > 0 && !isDescriptionEmpty();
    };

    // --- Draft validation (advisory) ---
    // Runs once per edited draft. Shows the guidance inline; the next click
    // submits anyway so users are guided, never blocked.
    const runDraftValidation = async () => {
        setValidating(true);
        try {
            const response = await validateFeedbackDraft({
                title: title.trim(),
                description,
                priority,
            });
            const assessment = response.data?.data || response.data;
            if (assessment && assessment.complete === false) {
                setGuidance(assessment);
                return false;
            }
            setGuidance(null);
            return true;
        } catch (err) {
            // Graceful degradation — never block submission on the check itself.
            logger.error("Draft validation error:", err);
            return true;
        } finally {
            setValidating(false);
        }
    };

    // --- Submit ---
    const handleSubmit = async () => {
        if (submitting || validating) return; // Guard against double-submit
        if (!canSubmit()) {
            message.warning("Please fill in title and description.");
            return;
        }

        if (!guidance) {
            const readyToSubmit = await runDraftValidation();
            if (!readyToSubmit) return;
        }

        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("title", title.trim());
            formData.append("description", description);
            formData.append("priority", priority);
            formData.append("pageUrl", envInfo.pageUrl);
            formData.append("userAgent", envInfo.userAgent);
            formData.append("screenResolution", envInfo.screenResolution);
            formData.append("language", envInfo.language);

            screenshots.forEach((file) => {
                formData.append("screenshots", file);
            });

            const response = await submitFeedback(formData);
            const result = response.data?.data || response.data;

            setSubmitResult(result);
            message.success("Feedback submitted successfully!");
        } catch (err) {
            logger.error("Submit error:", err);
            message.error("Failed to submit feedback. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // --- Footer buttons ---
    const getFooterButtons = () => {
        if (submitResult) {
            return [
                {
                    key: "close",
                    label: "Close",
                    variant: "primary",
                    onClick: onClose,
                },
            ];
        }

        return [
            {
                key: "cancel",
                label: "Cancel",
                variant: "secondary",
                hoverColor: "#FD3635",
                onClick: onClose,
                disabled: submitting,
            },
            {
                key: "submit",
                label: guidance ? "Submit anyway" : "Submit",
                variant: "primary",
                icon: <SendOutlined />,
                hoverColor: "#fad133",
                onClick: handleSubmit,
                loading: submitting || validating,
                disabled: !canSubmit(),
            },
        ];
    };

    // --- Render ---
    return (
        <Modal visible={visible} onClose={onClose} width="medium">
            <Modal.Header
                title="Help & Feedback"
                subtitle="Report an issue or request a feature"
            />
            <Modal.Body padding="default">
                {submitResult ? (
                    <div className="feedback-success">
                        <CheckCircleOutlined
                            style={{ fontSize: 48, color: "#4CAF50", marginBottom: 16 }}
                        />
                        <h3>Thank you for your feedback!</h3>
                        <p>
                            Your feedback has been recorded (
                            <a
                                href="/tasks/delegated"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onClose();
                                    navigate("/tasks/delegated");
                                }}
                                style={{ color: "#1B1B1B", textDecoration: "underline" }}
                            >
                                Task #{submitResult.taskId}
                            </a>
                            ) and will be reviewed by the tech team.
                        </p>
                    </div>
                ) : (
                    <div className="feedback-form">
                        {/* Completeness guidance (advisory — submission is never blocked) */}
                        {guidance && (
                            <div
                                role="alert"
                                data-testid="feedback-guidance"
                                style={{
                                    marginBottom: 16,
                                    padding: "10px 12px",
                                    border: "1px solid #fad133",
                                    borderLeft: "4px solid #fad133",
                                    background: "#FFFBE6",
                                    color: "#1B1B1B",
                                    borderRadius: 4,
                                }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                    A bit more detail will help the tech team
                                </div>
                                <div>{guidance.feedback}</div>
                                {guidance.suggestedPriority && guidance.suggestedPriority !== priority && (
                                    <div style={{ marginTop: 4 }}>
                                        Suggested priority: {guidance.suggestedPriority}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Title */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
                                Title <span style={{ color: "#FD3635" }}>*</span>
                            </label>
                            <Input
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    setGuidance(null);
                                }}
                                maxLength={120}
                                placeholder='e.g., "Leave form crashes when selecting half-day" or "Add bulk export for timesheets"'
                                showCount
                            />
                        </div>

                        {/* Description (Rich Text) */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
                                Description <span style={{ color: "#FD3635" }}>*</span>
                            </label>
                            <ReactQuill
                                theme="snow"
                                value={description}
                                onChange={(value) => {
                                    setDescription(value);
                                    setGuidance(null);
                                }}
                                modules={QUILL_MODULES}
                                formats={QUILL_FORMATS}
                                placeholder="Describe what you're experiencing or what you'd like to see..."
                                style={{ minHeight: 120 }}
                            />
                        </div>

                        {/* Priority */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
                                Priority
                            </label>
                            <Select
                                value={priority}
                                onChange={setPriority}
                                options={PRIORITY_OPTIONS}
                                style={{ width: 200 }}
                            />
                        </div>

                        {/* Screenshots */}
                        <div style={{ marginBottom: 8 }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
                                Screenshots
                            </label>
                            <ScreenshotUpload
                                files={screenshots}
                                onChange={setScreenshots}
                                disabled={submitting}
                            />
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer buttons={getFooterButtons()} showDefaultClose={false} />
        </Modal>
    );
};

export default HelpFeedbackModal;
